/**
 * Bundle configurator (<bundle-configurator>)
 *
 * Turns a product's option names/values into a step-by-step configurator and resolves the
 * exact variant through the native section render endpoint
 * (`/products/{handle}?section_id=…&option_values=…`), so products with more than 250
 * variants work. Feeds the resolved variant id and one line-item property per step into the
 * theme's existing <product-form> (assets/product-form.js), which handles add to cart.
 *
 * Data conventions the parser relies on (exact separators, see docs/bundle-configurator-naming-guide.md):
 *   " + " in an option NAME  -> the option holds several bundle items, one step per part
 *   " + " in a VALUE (name has one part) -> repeated step of the same item ("Pillow 1", "Pillow 2"), order-insensitive
 *   " - " in a value part    -> group, then choice (cascade: material -> colour)
 *   option with ONE value    -> fixed step, preselected, not editable
 *
 * The pure functions at the top are exported for Node tests (tests/unit, tests/matrix.mjs) via
 * a `module.exports` guard; the custom element is only defined in a browser.
 */
(function () {
  'use strict';

  var PART = ' + ';
  var LEVEL = ' - ';

  // Step visibility rules. Keys are parsed step labels, not handles.
  // A step in SHOW_WHEN shows only when every condition step has one of the listed values.
  // A rule applies only when the product has both the target step and the condition step.
  // A visible step is required. A hidden step is cleared and never sent to cart.
  // Changing Size or Pillow values in admin means updating this constant. One rule = one line.
  var SHOW_WHEN = {
    'Pillow 2': { Size: ['Queen', 'King', 'Split King', 'Super Split King'] },
  };

  // Line-item property names are prefixed so the theme's cart templates (drawer, cart page, cart
  // notification) hide them: they skip any property whose name starts with "_". The properties
  // still reach the order in admin and the order confirmation. Set to '' to show them in the cart.
  var PROPERTY_PREFIX = '_';

  // Gap the gallery's pagination dots keep from the bottom of the image. Mirrors
  // `.product__column-sticky .product_image_slider .swiper-pagination { bottom: 40px }` in sw--style.css.
  var DOT_OFFSET = 40;

  /* ------------------------------------------------------------------ */
  /* Pure functions                                                     */
  /* ------------------------------------------------------------------ */

  function valueName(value) {
    return typeof value === 'string' ? value : value.name;
  }

  function valueNames(option) {
    return option.values.map(valueName);
  }

  /**
   * @param {{name: string, values: Array<string|{name: string, id?: string|number}>}} option
   * @param {number} [optionIndex]
   * @returns {Array<object>} steps in part order
   */
  function buildSteps(option, optionIndex) {
    optionIndex = optionIndex || 0;
    var names = valueNames(option);
    var nameParts = option.name.split(PART);
    var base = { optionIndex: optionIndex, optionName: option.name };

    if (names.length === 1) {
      return [
        Object.assign({}, base, {
          key: optionIndex + '-0',
          partIndex: 0,
          label: option.name,
          type: 'fixed',
          value: names[0],
          optional: false,
          unordered: false,
        }),
      ];
    }

    var vals = names.map(function (v) {
      return v.split(PART).map(function (p) {
        return p.split(LEVEL);
      });
    });
    var maxParts = Math.max.apply(
      null,
      vals.map(function (v) {
        return v.length;
      })
    );
    var repeat = nameParts.length === 1 && maxParts > 1;
    var steps = [];

    for (var i = 0; i < maxParts; i++) {
      var label = repeat ? nameParts[0] + ' ' + (i + 1) : nameParts[i] || option.name + ' ' + (i + 1);
      var seen = {};
      var choices = [];
      vals.forEach(function (v) {
        if (!v[i]) return;
        var c = v[i].join(LEVEL);
        if (!seen[c]) {
          seen[c] = true;
          choices.push(c);
        }
      });
      var grouped = choices.some(function (c) {
        return c.indexOf(LEVEL) !== -1;
      });
      var step = Object.assign({}, base, {
        key: optionIndex + '-' + i,
        partIndex: i,
        label: label,
        type: grouped ? 'cascade' : 'choice',
        optional: vals.some(function (v) {
          return !v[i];
        }),
        unordered: repeat,
      });
      if (grouped) {
        step.groups = {};
        choices.forEach(function (c) {
          var segs = c.split(LEVEL);
          var g = segs[0];
          var rest = segs.slice(1).join(LEVEL);
          step.groups[g] = step.groups[g] || [];
          if (rest) step.groups[g].push(rest);
        });
      } else {
        step.choices = choices;
      }
      steps.push(step);
    }
    return steps;
  }

  function buildAllSteps(options) {
    var out = [];
    options.forEach(function (o, i) {
      out.push.apply(out, buildSteps(o, i));
    });
    return out;
  }

  /**
   * Map UI picks (one per part) to the option's exact value string.
   * Exact match only: "King" never matches "Super Split King".
   * Parts are sorted only for single-name options (repeated steps), so Pillow 1 = Low,
   * Pillow 2 = Standard resolves to "Standard + Low".
   * @returns {string|null}
   */
  function toValue(option, picks) {
    var parts = (picks || []).filter(Boolean);
    if (!parts.length) return null;
    var unordered = option.name.split(PART).length === 1;
    var norm = function (arr) {
      return (unordered ? arr.slice().sort() : arr).join('\0');
    };
    var want = norm(parts);
    var names = valueNames(option);
    for (var i = 0; i < names.length; i++) {
      if (norm(names[i].split(PART)) === want) return names[i];
    }
    return null;
  }

  function stepByLabel(steps, label) {
    for (var i = 0; i < steps.length; i++) if (steps[i].label === label) return steps[i];
    return null;
  }

  function isStepVisible(step, selection, steps) {
    var rule = SHOW_WHEN[step.label];
    if (!rule) return true;
    return Object.keys(rule).every(function (depLabel) {
      var dep = stepByLabel(steps, depLabel);
      if (!dep) return true; // product lacks the condition step: rule does not apply
      return rule[depLabel].indexOf(selection[dep.key]) !== -1;
    });
  }

  function visibleSteps(steps, selection) {
    return steps.filter(function (s) {
      return isStepVisible(s, selection, steps);
    });
  }

  function stepValue(step, selection) {
    return step.type === 'fixed' ? step.value : selection[step.key] || '';
  }

  function stepValuesForOption(steps, selection, optionIndex) {
    return steps
      .filter(function (s) {
        return s.optionIndex === optionIndex;
      })
      .map(function (s) {
        return isStepVisible(s, selection, steps) ? stepValue(s, selection) : '';
      });
  }

  /** Every visible step has a value (visible == required; fixed steps always count). */
  function isComplete(steps, selection) {
    return visibleSteps(steps, selection).every(function (s) {
      return Boolean(stepValue(s, selection));
    });
  }

  /** One exact option value per option, or null where no exact value exists. */
  function resolveValues(options, steps, selection) {
    return options.map(function (o, i) {
      return toValue(o, stepValuesForOption(steps, selection, i));
    });
  }

  /**
   * Label shown to the customer and used as the cart property name.
   * A repeated step drops its number when it is the only visible step of its option
   * ("Pillow" instead of "Pillow 1" on Single / Super Single). SHOW_WHEN keys still use `step.label`.
   */
  function displayLabel(step, steps, selection) {
    if (!step.unordered) return step.label;
    var siblings = visibleSteps(steps, selection).filter(function (s) {
      return s.optionIndex === step.optionIndex;
    });
    return siblings.length === 1 ? step.optionName : step.label;
  }

  /** Ordered [label, value] pairs, one per visible step. */
  function collectProperties(steps, selection) {
    return visibleSteps(steps, selection).map(function (s) {
      return [displayLabel(s, steps, selection), stepValue(s, selection)];
    });
  }

  /**
   * Helper copy for a step's current value, from the section's `texts` payload.
   * Scopes are tried most specific first: the step's own label ("Bedding Set"), then its option
   * name ("Pillow", which a numbered step like "Pillow 1" falls back to), then unscoped. Within a
   * scope the full value comes first, then its last " - " segment, so a cascade value
   * "Egyptian Cotton - Navy" can pick up copy written for "Navy".
   * @param {Object<string, string>} texts map keyed `scope + "\0" + label`
   * @returns {string} html, or '' when nothing matches
   */
  function textFor(texts, step, value) {
    if (!texts || !step || !value) return '';
    var scopes = [step.label, step.optionName, ''];
    var labels = [value];
    var last = value.split(LEVEL).pop();
    if (last !== value) labels.push(last);
    for (var i = 0; i < scopes.length; i++) {
      for (var j = 0; j < labels.length; j++) {
        var hit = texts[scopes[i] + '\0' + labels[j]];
        if (hit) return hit;
      }
    }
    return '';
  }

  /** Option value ids (from product.options_with_values) for exact value strings; null when missing. */
  function optionValueIds(options, values) {
    return values.map(function (val, i) {
      var list = options[i] ? options[i].values : [];
      for (var j = 0; j < list.length; j++) {
        if (valueName(list[j]) === val) return typeof list[j] === 'object' && list[j].id != null ? String(list[j].id) : null;
      }
      return null;
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      PART: PART,
      LEVEL: LEVEL,
      PROPERTY_PREFIX: PROPERTY_PREFIX,
      SHOW_WHEN: SHOW_WHEN,
      buildSteps: buildSteps,
      buildAllSteps: buildAllSteps,
      toValue: toValue,
      isStepVisible: isStepVisible,
      visibleSteps: visibleSteps,
      stepValue: stepValue,
      stepValuesForOption: stepValuesForOption,
      isComplete: isComplete,
      displayLabel: displayLabel,
      resolveValues: resolveValues,
      collectProperties: collectProperties,
      optionValueIds: optionValueIds,
      textFor: textFor,
    };
  }

  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('bundle-configurator')) return;

  /* ------------------------------------------------------------------ */
  /* Custom element                                                     */
  /* ------------------------------------------------------------------ */

  class BundleConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      var dataEl = this.querySelector('[data-bundle-options]');
      if (!dataEl) return;
      try {
        this._data = JSON.parse(dataEl.textContent);
      } catch (err) {
        console.error('[bundle-configurator] invalid options JSON', err);
        return;
      }
      this._strings = this._data.strings || {};
      // [label, url] pairs from the section → { label: url }. Same metaobject source as the variant picker.
      this._images = {};
      (this._data.images || []).forEach(function (pair) {
        if (pair && pair[0] && pair[1] && !this._images[pair[0]]) this._images[pair[0]] = pair[1];
      }, this);
      // Step/option names rendered as bordered text buttons instead of icon pills (section setting).
      this._buttonSteps = (this._data.buttonSteps || []).map(function (n) {
        return String(n).trim().toLowerCase();
      }).filter(Boolean);
      // [label, css-background] pairs → { label: css }. Shopify swatches first, then the colour map.
      this._swatches = {};
      (this._data.swatches || []).forEach(function (pair) {
        if (pair && pair[0] && pair[1] && !this._swatches[pair[0]]) this._swatches[pair[0]] = pair[1];
      }, this);
      // [scope, label, html] triples from the section → { "scope\0label": html }. Metafield copy
      // shown under a step once its value is picked; empty on products without those metafields.
      this._texts = {};
      (this._data.texts || []).forEach(function (triple) {
        if (!triple || !triple[2]) return;
        var key = String(triple[0] || '') + '\0' + String(triple[1] || '');
        if (!this._texts[key]) this._texts[key] = triple[2];
      }, this);
      this._steps = buildAllSteps(this._data.options || []);
      this._selection = {};
      this._groupSel = {};
      this._variant = null;
      this._abort = null;

      this._section = this.closest('[data-section]') || document;
      this._priceEl = this._section.querySelector('[data-bundle-price]');
      this._fromPriceHtml = this._priceEl ? this._priceEl.innerHTML : '';
      this._atcPriceEl = this.querySelector('[data-bundle-atc-price]');
      this._atcFromHtml = this._atcPriceEl ? this._atcPriceEl.innerHTML : '';
      this._stepsEl = this.querySelector('[data-bundle-steps]');
      this._propsEl = this.querySelector('[data-bundle-properties]');
      this._textsEl = this.querySelector('[data-bundle-texts]');

      this._renderSteps();
      this._onChange = this._onChange.bind(this);
      this.addEventListener('change', this._onChange);
      this._ready = true;

      this._update();
      this._watchGalleryDots();
      // <product-form> resets the button label in its own connectedCallback; re-apply ours after it upgrades.
      var self = this;
      customElements.whenDefined('product-form').then(function () {
        requestAnimationFrame(function () {
          self._update();
        });
      });
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      this.removeEventListener('change', this._onChange);
      if (this._placeDots) window.removeEventListener('resize', this._placeDots);
    }

    /* ---------- rendering ---------- */

    _stepTitle(step) {
      var label = displayLabel(step, this._steps, this._selection);
      var prefix = this._strings.stepPrefix;
      if (step.type === 'fixed' || !prefix) return label;
      return prefix + ' ' + label;
    }

    _renderSteps() {
      if (!this._stepsEl) return;
      var self = this;
      var sid = String(this._data.sectionId || 'bundle');
      // Keep the section's "Fixed step" blocks: they are rendered in Liquid, not from the options.
      Array.prototype.forEach.call(this._stepsEl.querySelectorAll('.bundle-step:not([data-static-step])'), function (el) {
        el.remove();
      });

      this._steps.forEach(function (step) {
        var fs = document.createElement('fieldset');
        fs.className = 'bundle-step product-form__input product-form__input--pill';
        fs.dataset.step = step.key;
        fs.dataset.stepType = step.type;
        fs.dataset.stepLabel = step.label;

        var legend = document.createElement('legend');
        legend.className = 'form__label bundle-step__legend';
        var text = document.createElement('span');
        text.className = 'bundle-step__label';
        text.textContent = self._stepTitle(step);
        legend.appendChild(text);
        fs.appendChild(legend);

        if (step.type === 'fixed') {
          fs.classList.add('bundle-step--fixed');
          var fixed = document.createElement('div');
          fixed.className = 'bundle-step__fixed';
          var val = document.createElement('span');
          val.className = 'bundle-step__fixed-value';
          val.textContent = step.value;
          fixed.appendChild(val);
          if (self._strings.included) {
            var tag = document.createElement('span');
            tag.className = 'bundle-step__included';
            tag.textContent = self._strings.included;
            fixed.appendChild(tag);
          }
          fs.appendChild(fixed);
        } else if (step.type === 'choice') {
          var asButton = self._isButtonStep(step);
          var asSwatch = !asButton && self._allSwatches(step.choices, null);
          // Nothing to show in a pill: no icon for any choice and no swatch. Fall back to text buttons.
          if (!asButton && !asSwatch && !self._anyIcon(step.choices)) asButton = true;
          if (asSwatch) {
            fs.classList.remove('product-form__input--pill');
            fs.classList.add('product-form__input--swatch', 'bundle-step--swatch');
            legend.appendChild(self._valueSpan());
          }
          if (asButton) fs.classList.add('bundle-step--buttons', 'product-type-option');
          var wrap = document.createElement('div');
          wrap.className = 'bundle-step__choices' + (asSwatch ? ' bundle-step__choices--swatch product-form__input--swatch' : '');
          step.choices.forEach(function (c, i) {
            var rid = sid + '-' + step.key + '-' + i;
            var rname = 'bundle-' + sid + '-' + step.key;
            if (asSwatch) self._appendSwatch(wrap, step, c, self._swatchFor(c, null), rid, rname, false);
            else if (asButton) self._appendButton(wrap, step, 'choice', c, rid, rname, false);
            else self._appendRadio(wrap, step, 'choice', c, rid, rname, false);
          });
          fs.appendChild(wrap);
        } else {
          var groups = Object.keys(step.groups);
          var groupsAsButton = self._isButtonStep(step);
          if (!groupsAsButton && !self._anyIcon(groups)) groupsAsButton = true;
          var gWrap = document.createElement('div');
          gWrap.className = 'bundle-step__groups';
          gWrap.setAttribute('role', 'group');
          groups.forEach(function (g, i) {
            var gid = sid + '-' + step.key + '-g' + i;
            var gname = 'bundle-' + sid + '-' + step.key + '-group';
            if (groupsAsButton) self._appendButton(gWrap, step, 'group', g, gid, gname, false);
            else self._appendRadio(gWrap, step, 'group', g, gid, gname, false);
          });
          fs.appendChild(gWrap);
          var childrenAsSwatch = groups.every(function (g) {
            return self._allSwatches(step.groups[g], g);
          });
          if (childrenAsSwatch) fs.classList.add('bundle-step--swatch-children');
          var childrenAsButton = !childrenAsSwatch && groups.every(function (g) {
            return !self._anyIcon(step.groups[g]);
          });
          if (groupsAsButton || childrenAsButton) fs.classList.add('bundle-step--buttons', 'product-type-option');
          groups.forEach(function (g, gi) {
            var cWrap = document.createElement('div');
            cWrap.className = 'bundle-step__choices bundle-step__choices--cascade' + (childrenAsSwatch ? ' bundle-step__choices--swatch product-form__input--swatch' : '');
            cWrap.dataset.group = g;
            cWrap.hidden = true;
            cWrap.setAttribute('role', 'group');
            cWrap.setAttribute('aria-label', g);
            var cLabel = document.createElement('p');
            cLabel.className = 'form__label bundle-step__legend bundle-step__choices-label';
            cLabel.setAttribute('aria-hidden', 'true'); // the wrapper's aria-label already names the group
            // Same span as a step legend, so one .bundle-step__label rule styles every label.
            var cText = document.createElement('span');
            cText.className = 'bundle-step__label';
            cText.textContent = self._strings.cascadeLabel || 'Choose Color';
            cLabel.appendChild(cText);
            cLabel.appendChild(self._valueSpan());
            cWrap.appendChild(cLabel);
            step.groups[g].forEach(function (c, i) {
              if (childrenAsSwatch) self._appendSwatch(cWrap, step, c, self._swatchFor(c, g), sid + '-' + step.key + '-' + gi + '-' + i, 'bundle-' + sid + '-' + step.key + '-' + gi, true);
              else if (childrenAsButton) self._appendButton(cWrap, step, 'choice', c, sid + '-' + step.key + '-' + gi + '-' + i, 'bundle-' + sid + '-' + step.key + '-' + gi, true);
              else self._appendRadio(cWrap, step, 'choice', c, sid + '-' + step.key + '-' + gi + '-' + i, 'bundle-' + sid + '-' + step.key + '-' + gi, true);
            });
            fs.appendChild(cWrap);
          });
        }
        self._stepsEl.appendChild(fs);
      });

      // Static blocks render before the parsed steps in the DOM; move them after, where a fixed
      // option step would sit.
      Array.prototype.forEach.call(this._stepsEl.querySelectorAll('[data-static-step]'), function (el) {
        self._stepsEl.appendChild(el);
      });
    }

    /** Step listed in the "Text-button steps" setting, by step label or option name (case-insensitive). */
    _isButtonStep(step) {
      var label = step.label.toLowerCase();
      var option = step.optionName.toLowerCase();
      return this._buttonSteps.some(function (n) {
        return n === label || n === option;
      });
    }

    /** Bordered text button, same classes as the Type option in snippets/product-variant-options.liquid. */
    _appendButton(parent, step, role, value, id, name, disabled) {
      var input = document.createElement('input');
      input.type = 'radio';
      input.id = id;
      input.name = name;
      input.value = value;
      input.dataset.role = role;
      input.dataset.step = step.key;
      input.disabled = Boolean(disabled);
      input.setAttribute('autocomplete', 'off');
      var label = document.createElement('label');
      // Same class set as live's `label.no-img cm_product_vraint_in`; all box styling lives in
      // component-bundle-configurator.css, so no Tailwind utilities to override.
      label.className = 'cm_product_vraint_in bundle-choice--button';
      label.style.maxWidth = 'unset';
      label.htmlFor = id;
      label.textContent = value;
      parent.appendChild(input);
      parent.appendChild(label);
    }

    /** Swatch css for a choice: "Group - Choice" first, then the bare choice. Null when none. */
    _swatchFor(choice, group) {
      if (group && this._swatches[group + LEVEL + choice]) return this._swatches[group + LEVEL + choice];
      return this._swatches[choice] || null;
    }

    /** True when at least one of these values has a metaobject icon. */
    _anyIcon(values) {
      var self = this;
      return values.some(function (v) {
        return Boolean(self._images[v]);
      });
    }

    _allSwatches(choices, group) {
      var self = this;
      return choices.length > 0 && choices.every(function (c) {
        return Boolean(self._swatchFor(c, group));
      });
    }

    _valueSpan() {
      var span = document.createElement('span');
      span.className = 'bundle-step__value';
      span.setAttribute('data-selected-value', '');
      return span;
    }

    /** Same markup as snippets/swatch-input.liquid + snippets/swatch.liquid (circle). */
    _appendSwatch(parent, step, value, css, id, name, disabled) {
      var input = document.createElement('input');
      input.type = 'radio';
      input.id = id;
      input.name = name;
      input.value = value;
      input.className = 'swatch-input__input';
      input.dataset.role = 'choice';
      input.dataset.step = step.key;
      input.disabled = Boolean(disabled);
      input.setAttribute('autocomplete', 'off');
      var label = document.createElement('label');
      label.htmlFor = id;
      label.title = value;
      label.className = 'swatch-input__label';
      var swatch = document.createElement('span');
      swatch.className = 'swatch';
      swatch.style.setProperty('--swatch--background', css);
      var text = document.createElement('span');
      text.className = 'visually-hidden';
      text.textContent = value;
      label.appendChild(swatch);
      label.appendChild(text);
      parent.appendChild(input);
      parent.appendChild(label);
    }

    _appendRadio(parent, step, role, value, id, name, disabled) {
      var input = document.createElement('input');
      input.type = 'radio';
      input.id = id;
      input.name = name;
      input.value = value;
      input.dataset.role = role;
      input.dataset.step = step.key;
      input.disabled = Boolean(disabled);
      input.setAttribute('autocomplete', 'off');
      var label = document.createElement('label');
      label.className = 'cm_product_vraint_in';
      label.htmlFor = id;
      var iconUrl = this._images[value];
      if (iconUrl) {
        var img = document.createElement('img');
        img.className = 'variant-image';
        img.src = iconUrl;
        img.alt = '';
        // Same intrinsic box as snippets/product-variant-options.liquid (36x24); CSS pins it to 60x40.
        img.width = 36;
        img.height = 24;
        img.loading = 'lazy';
        label.appendChild(img);
        label.classList.add('bundle-choice--has-icon');
      } else {
        // No metaobject icon: grey placeholder box so every pill has the same footprint.
        var box = document.createElement('span');
        box.className = 'variant-image variant-image--placeholder';
        box.setAttribute('aria-hidden', 'true');
        label.appendChild(box);
        label.classList.add('bundle-choice--no-icon');
      }
      var text = document.createElement('span');
      text.className = 'bundle-choice__text';
      text.textContent = value;
      label.appendChild(text);
      parent.appendChild(input);
      parent.appendChild(label);
    }

    /* ---------- state ---------- */

    _onChange(event) {
      var input = event.target;
      if (!input || input.type !== 'radio' || !input.dataset || !input.dataset.step) return;
      var step = this._stepByKey(input.dataset.step);
      if (!step) return;

      if (input.dataset.role === 'group') {
        // Material change always clears the colour, including White.
        this._groupSel[step.key] = input.value;
        var children = step.groups[input.value] || [];
        this._selection[step.key] = children.length ? '' : input.value;
      } else if (step.type === 'cascade') {
        this._selection[step.key] = this._groupSel[step.key] + LEVEL + input.value;
      } else {
        this._selection[step.key] = input.value;
      }
      this._update();
    }

    _stepByKey(key) {
      for (var i = 0; i < this._steps.length; i++) if (this._steps[i].key === key) return this._steps[i];
      return null;
    }

    _update() {
      this._applyRules();
      this._syncDom();
      this._syncTexts();
      this._syncProperties();
      this._filterMedia();
      this._resolve();
    }

    /**
     * Media option filter (assets/media-option-filter.js): the steps are not option values, so map
     * the current picks back onto the product's own options first. No-op unless the section setting
     * `filter_media_by_option` is on and the product's alt texts carry "Option: Value" tags.
     */
    _filterMedia(featuredMediaId) {
      var filter = this._section && this._section._mediaOptionFilter;
      if (!filter) return;
      var self = this;
      var selected = {};
      (this._data.options || []).forEach(function (option, i) {
        var value = toValue(option, stepValuesForOption(self._steps, self._selection, i));
        if (value) selected[option.name] = value;
      });
      filter.apply(selected, featuredMediaId);
    }

    _applyRules() {
      var self = this;
      this._steps.forEach(function (s) {
        if (!isStepVisible(s, self._selection, self._steps)) {
          delete self._selection[s.key];
          delete self._groupSel[s.key];
        }
      });
    }

    _syncDom() {
      if (!this._stepsEl) return;
      var self = this;
      this._steps.forEach(function (step) {
        var fs = self._stepsEl.querySelector('[data-step="' + step.key + '"]');
        if (!fs) return;
        var visible = isStepVisible(step, self._selection, self._steps);
        fs.hidden = !visible;
        if (visible) {
          var title = fs.querySelector('legend .bundle-step__label');
          var wanted = self._stepTitle(step);
          if (title && title.textContent !== wanted) title.textContent = wanted;
        }
        if (step.type === 'fixed') return;

        var value = self._selection[step.key] || '';
        var group = self._groupSel[step.key] || '';
        fs.classList.toggle('bundle-step--done', Boolean(value));
        Array.prototype.forEach.call(fs.querySelectorAll('.bundle-step__value'), function (valueEl) {
          valueEl.textContent = value ? value.split(LEVEL).pop() : '';
        });

        Array.prototype.forEach.call(fs.querySelectorAll('input[type="radio"]'), function (input) {
          if (input.dataset.role === 'group') {
            input.checked = input.value === group;
            input.disabled = !visible;
          } else if (step.type === 'cascade') {
            var wrap = input.closest('[data-group]');
            var active = Boolean(wrap) && wrap.dataset.group === group;
            input.disabled = !visible || !active;
            input.checked = active && value === group + LEVEL + input.value;
          } else {
            input.checked = input.value === value;
            input.disabled = !visible;
          }
        });
        Array.prototype.forEach.call(fs.querySelectorAll('[data-group]'), function (w) {
          w.hidden = w.dataset.group !== group;
        });
      });
    }

    /**
     * Metafield helper copy for the whole selection, rendered under the steps and above the
     * add-to-cart button. One block per visible step that has copy, in step order. Each block
     * carries the same classes as the live PDP pickers (.size-custom-text + Tailwind utilities)
     * so the type and spacing match the mattress and pillow pages.
     */
    _syncTexts() {
      if (!this._textsEl) return;
      var self = this;
      var blocks = [];
      visibleSteps(this._steps, this._selection).forEach(function (step) {
        var copy = textFor(self._texts, step, stepValue(step, self._selection));
        if (copy) blocks.push([step.label, copy]);
      });
      // Rebuilding on every change would drop the rendered nodes needlessly; only redraw on a change.
      var signature = JSON.stringify(blocks);
      if (this._textsSig === signature) return;
      this._textsSig = signature;
      this._textsEl.textContent = '';
      blocks.forEach(function (pair) {
        var block = document.createElement('div');
        block.className = 'bundle-configurator__text size-custom-text text-[12px] text-gray-600 mt-2';
        block.setAttribute('data-step-text', pair[0]);
        block.innerHTML = pair[1];
        self._textsEl.appendChild(block);
      });
      this._textsEl.hidden = blocks.length === 0;
    }

    /**
     * Keep the gallery's pagination dots over the image, the way they sit on the standard PDP.
     * Swiper sizes the container to the tallest slide (global.js initialises it without autoHeight)
     * and sw--style.css pins the dots 40px from the container's bottom, so on a product whose images
     * differ in aspect ratio the dots drop into the empty space below a shorter image. Offsetting
     * them by the gap between container and active slide puts them back over the image. Nothing about
     * how the image is sized changes: each slide keeps its own `--ratio` and is never cropped.
     */
    _placeGalleryDots() {
      if (!this._slider) return;
      var pagination = this._slider.querySelector('.swiper-pagination');
      var media = this._slider.querySelector('.swiper-slide-active .product__media') || this._slider.querySelector('.product__media');
      if (!pagination || !media) return;
      var gap = this._slider.getBoundingClientRect().height - media.getBoundingClientRect().height;
      pagination.style.bottom = DOT_OFFSET + Math.max(0, Math.round(gap)) + 'px';
    }

    /** global.js initialises the gallery swiper on its own schedule, so wait for it, then give up. */
    _watchGalleryDots(attempt) {
      var slider = this._section.querySelector ? this._section.querySelector('.product_image_slider') : null;
      if (!slider || !slider.swiper) {
        if ((attempt || 0) < 20) setTimeout(this._watchGalleryDots.bind(this, (attempt || 0) + 1), 150);
        return;
      }
      this._slider = slider;
      this._placeDots = this._placeGalleryDots.bind(this);
      slider.swiper.on('slideChange', this._placeDots);
      slider.swiper.on('resize', this._placeDots);
      slider.swiper.on('imagesReady', this._placeDots);
      window.addEventListener('resize', this._placeDots);
      this._placeDots();
    }

    _syncProperties() {
      if (!this._propsEl) return;
      var self = this;
      while (this._propsEl.firstChild) this._propsEl.removeChild(this._propsEl.firstChild);
      collectProperties(this._steps, this._selection).forEach(function (pair) {
        if (!pair[1]) return;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + PROPERTY_PREFIX + pair[0] + ']';
        input.value = pair[1];
        self._propsEl.appendChild(input);
      });
    }

    /* ---------- variant resolution ---------- */

    _resolve() {
      if (this._abort) this._abort.abort();
      this._abort = null;

      if (!isComplete(this._steps, this._selection)) return this._setState('choose');

      var values = resolveValues(this._data.options, this._steps, this._selection);
      if (values.some(function (v) { return v === null; })) return this._setState('unavailable');
      var ids = optionValueIds(this._data.options, values);
      if (ids.some(function (id) { return !id; })) return this._setState('unavailable');

      var url =
        this._data.productUrl +
        '?section_id=' + encodeURIComponent(this._data.sectionId) +
        '&option_values=' + ids.join(',');

      var self = this;
      var controller = new AbortController();
      this._abort = controller;
      this._setPending();
      this._setLoading(true);

      fetch(url, { signal: controller.signal })
        .then(function (response) { return response.text(); })
        .then(function (text) {
          if (controller.signal.aborted) return;
          var html = new DOMParser().parseFromString(text, 'text/html');
          var node = html.querySelector('bundle-configurator [data-selected-variant]');
          var variant = null;
          try {
            variant = node && node.textContent.trim() ? JSON.parse(node.textContent) : null;
          } catch (err) {
            variant = null;
          }
          // Exact-match guard: the returned variant must carry exactly the requested option values.
          var matches = Boolean(variant) && values.every(function (v, i) { return variant['option' + (i + 1)] === v; });
          if (!matches) return self._setState('unavailable');
          self._swapPrice(html);
          self._setVariant(variant);
          self._setMedia(variant);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          console.error('[bundle-configurator]', err);
          self._setState('unavailable');
        })
        .finally(function () {
          if (self._abort === controller) {
            self._abort = null;
            self._setLoading(false);
          }
        });
    }

    _swapPrice(html) {
      if (this._priceEl) {
        var src = html.querySelector('[data-bundle-price]');
        if (src) this._priceEl.innerHTML = src.innerHTML;
      }
      if (this._atcPriceEl) {
        var atc = html.querySelector('[data-bundle-atc-price]');
        if (atc) this._atcPriceEl.innerHTML = atc.innerHTML;
      }
    }

    _setVariant(variant) {
      this._variant = variant;
      var idInput = this._idInput();
      if (idInput) {
        idInput.value = String(variant.id);
        idInput.disabled = false;
      }
      // Publish BEFORE toggling: product-form's MutationObserver re-labels the button unless it knows
      // the selected variant is unavailable.
      this._publishVariant(variant);
      this._toggleSubmit(!variant.available, variant.available ? this._strings.addToCart : this._strings.soldOut);
      this.dataset.state = variant.available ? 'ready' : 'sold-out';
    }

    /** A fetch is in flight: never leave the previous variant id addable. Label is kept as-is. */
    _setPending() {
      this._variant = null;
      var idInput = this._idInput();
      if (idInput) {
        idInput.value = '';
        idInput.disabled = true;
      }
      this._publishVariant({ id: null, available: false });
      this._toggleSubmit(true);
      this.dataset.state = 'pending';
    }

    _setState(state) {
      this._variant = null;
      var idInput = this._idInput();
      if (idInput) {
        idInput.value = '';
        idInput.disabled = true;
      }
      if (this._priceEl) this._priceEl.innerHTML = this._fromPriceHtml;
      if (this._atcPriceEl) this._atcPriceEl.innerHTML = this._atcFromHtml;
      this._publishVariant({ id: null, available: false });
      this._toggleSubmit(true, state === 'choose' ? this._strings.choose : this._strings.unavailable);
      var form = this._form();
      if (form && typeof form.handleErrorMessage === 'function') form.handleErrorMessage();
      this.dataset.state = state;
    }

    _setMedia(variant) {
      if (!variant || !variant.featured_media || !variant.featured_media.id) return;
      // Re-run the filter with the resolved variant so its own image leads the filtered set.
      this._filterMedia(variant.featured_media.id);
      var gallery = this._section.querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
      try {
        gallery.setActiveMedia(this._data.sectionId + '-' + variant.featured_media.id, true);
      } catch (err) {
        /* gallery is optional */
      }
    }

    _setLoading(on) {
      this.classList.toggle('bundle-configurator--loading', on);
      if (this._priceEl) this._priceEl.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    _publishVariant(variant) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      publish(PUB_SUB_EVENTS.variantChange, {
        data: { sectionId: this._data.sectionId, html: null, variant: variant },
      });
    }

    _toggleSubmit(disable, text) {
      var form = this._form();
      if (form && typeof form.toggleSubmitButton === 'function') {
        form.toggleSubmitButton(disable, text);
        return;
      }
      var btn = this.querySelector('.product-form__submit');
      if (!btn) return;
      if (disable) btn.setAttribute('disabled', 'disabled');
      else btn.removeAttribute('disabled');
      var span = btn.querySelector('span');
      if (span && text) span.textContent = text;
    }

    _form() {
      return this.querySelector('product-form');
    }

    _idInput() {
      return this.querySelector('form input[name="id"]');
    }
  }

  customElements.define('bundle-configurator', BundleConfigurator);
})();
